/**
 * Permission catalogue. Code checks these keys; tenants combine them into roles.
 * Must match the rows seeded in packages/db/migrations (a test enforces this).
 * Adding a permission = add it here AND in a new migration.
 */
export const PERMISSIONS = [
  "settings.view",
  "settings.manage",
  "branches.view",
  "branches.manage",
  "staff.view",
  "staff.manage",
  "roles.manage",
  "doctors.view",
  "doctors.manage",
  "availability.view",
  "availability.manage",
  "services.view",
  "services.manage",
  "tax.manage",
  "patients.view",
  "patients.create",
  "patients.edit",
  "patients.delete",
  "patients.merge",
  "patients.export",
  "appointments.view",
  "appointments.create",
  "appointments.edit",
  "appointments.cancel",
  "appointments.checkin",
  "waitlist.manage",
  "clinical.view",
  "clinical.create",
  "clinical.edit",
  "clinical.sign",
  "treatment_plans.manage",
  "prescriptions.create",
  "documents.view",
  "documents.upload",
  "documents.delete",
  "consents.manage",
  "billing.view",
  "billing.create",
  "billing.discount",
  "billing.void",
  "payments.create",
  "refunds.create",
  "billing.settings",
  "reports.view",
  "reports.financial",
  "data.export",
  "communications.view",
  "communications.send",
  "templates.manage",
  "automations.manage",
  "campaigns.manage",
  "crm.manage",
  "inventory.view",
  "inventory.manage",
  "labs.manage",
  "insurance.manage",
  "audit.view",
  "ai.use",
  "therapy.view",
  "therapy.manage",
  "therapy.record",
  "pharmacy.dispense",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const PERMISSION_SET = new Set<string>(PERMISSIONS);
export const isPermission = (value: string): value is Permission => PERMISSION_SET.has(value);

/**
 * Default roles created for every new tenant. They are starting points, not hard-coded rules:
 * after onboarding each clinic can edit them (except the owner role, which always has everything).
 */
export interface RoleTemplate {
  key: string;
  name: string;
  description: string;
  isOwner?: boolean;
  permissions: readonly Permission[] | "all";
  /** Only created for clinics of this type (e.g. Therapist for Ayurveda). */
  specialty?: "dental" | "ayurveda";
  /** Extra permissions for clinics of a given type (e.g. therapy access for an Ayurveda physician). */
  extra?: Partial<Record<"dental" | "ayurveda", readonly Permission[]>>;
}

const view = ["settings.view", "branches.view", "staff.view", "doctors.view", "availability.view", "services.view"] as const;

export const ROLE_TEMPLATES: readonly RoleTemplate[] = [
  { key: "owner", name: "Clinic owner", description: "Full access, including roles and billing settings.", isOwner: true, permissions: "all" },
  {
    key: "admin",
    name: "Administrator",
    description: "Runs the clinic day to day. Everything except role design and the audit log.",
    permissions: PERMISSIONS.filter((p) => p !== "roles.manage" && p !== "audit.view" && p !== "patients.merge"),
  },
  {
    key: "dentist",
    name: "Dentist",
    description: "Clinical work for their patients, plus prescriptions and treatment plans.",
    permissions: [
      ...view,
      "patients.view", "patients.create", "patients.edit",
      "appointments.view", "appointments.create", "appointments.edit", "appointments.checkin",
      "clinical.view", "clinical.create", "clinical.edit", "clinical.sign", "treatment_plans.manage", "prescriptions.create",
      "documents.view", "documents.upload", "consents.manage",
      "billing.view", "billing.create", "reports.view", "communications.view", "communications.send", "inventory.view",
      "labs.manage", "ai.use",
    ],
    extra: { ayurveda: ["therapy.view", "therapy.manage", "therapy.record"] },
  },
  {
    key: "receptionist",
    name: "Receptionist",
    description: "Front desk: patients, appointments, queue, payments. No clinical notes.",
    permissions: [
      ...view,
      "patients.view", "patients.create", "patients.edit",
      "appointments.view", "appointments.create", "appointments.edit", "appointments.cancel", "appointments.checkin", "waitlist.manage",
      "documents.upload", "consents.manage",
      "billing.view", "billing.create", "payments.create",
      "communications.view", "communications.send", "crm.manage",
    ],
    extra: { ayurveda: ["therapy.view", "therapy.manage", "pharmacy.dispense"] },
  },
  {
    key: "assistant",
    name: "Nurse / dental assistant",
    description: "Chairside support: queue, clinical viewing and chart entries, documents.",
    permissions: [
      ...view,
      "patients.view", "appointments.view", "appointments.checkin",
      "clinical.view", "clinical.create", "documents.view", "documents.upload", "consents.manage",
      "inventory.view", "inventory.manage", "labs.manage",
    ],
    extra: { ayurveda: ["therapy.view", "therapy.record"] },
  },
  {
    key: "accountant",
    name: "Accountant",
    description: "Billing, payments, refunds and financial reports. No clinical access.",
    permissions: [
      ...view,
      "patients.view", "appointments.view",
      "billing.view", "billing.create", "billing.void", "billing.discount", "payments.create", "refunds.create", "billing.settings",
      "reports.view", "reports.financial", "data.export", "insurance.manage", "inventory.view",
    ],
  },
  {
    key: "marketing",
    name: "Marketing",
    description: "Campaigns, leads and treatment conversion. No clinical or financial detail.",
    permissions: [
      "settings.view", "branches.view", "doctors.view", "services.view",
      "patients.view", "communications.view", "communications.send", "templates.manage", "campaigns.manage", "crm.manage",
      "reports.view",
    ],
  },
  {
    key: "therapist",
    name: "Therapist",
    description: "Therapy staff: their sessions on the therapy board, start / finish and oil used.",
    specialty: "ayurveda",
    permissions: ["settings.view", "branches.view", "therapy.view", "therapy.record"],
  },
  {
    key: "pharmacist",
    name: "Pharmacist",
    description: "Pharmacy counter: dispense prescriptions, stock and batches, pharmacy bills and payments.",
    specialty: "ayurveda",
    permissions: ["settings.view", "branches.view", "patients.view", "inventory.view", "inventory.manage", "pharmacy.dispense", "billing.view", "payments.create"],
  },
];

export function templatePermissions(template: RoleTemplate, specialty?: "dental" | "ayurveda"): readonly Permission[] {
  const base = template.permissions === "all" ? PERMISSIONS : template.permissions;
  const extra = specialty ? (template.extra?.[specialty] ?? []) : [];
  return [...new Set([...base, ...extra])];
}
